'use server';

import {
  generateAuthenticationOptions,
  generateRegistrationOptions,
  verifyAuthenticationResponse,
  verifyRegistrationResponse,
} from '@simplewebauthn/server';
import { MAX_PASSKEYS, hashAccessKey, isAccessKey } from '@/lib/accounts';
import { endSession, isRateLimited, startSession } from '@/lib/auth';
import { createAdminClient } from '@/lib/supabase/admin';
import {
  RP_NAME,
  fromBase64Url,
  getRelyingParty,
  saveChallenge,
  takeChallenge,
  toBase64Url,
} from '@/lib/webauthn';
import { redirect } from 'next/navigation';

const INVALID_KEY = 'Invalid access key. Try again.';
const RATE_LIMITED = 'Too many attempts. Wait a minute and try again.';
const EXPIRED = 'This request expired. Try again.';

export async function beginActivation(rawKey) {
  if (await isRateLimited()) return { error: RATE_LIMITED };

  const key = String(rawKey ?? '').trim();
  if (!isAccessKey(key)) return { error: INVALID_KEY };

  const admin = createAdminClient();
  const { data: accessKey } = await admin
    .from('access_keys')
    .select('id, user_id, used_at, revoked, expires_at, passkey, profiles(username)')
    .eq('key_hash', hashAccessKey(key))
    .maybeSingle();

  if (!accessKey || accessKey.revoked) return { error: INVALID_KEY };
  if (accessKey.expires_at && new Date(accessKey.expires_at) <= new Date()) {
    return { error: 'This key has expired.' };
  }

  // Clé sans passkey : connexion directe, réutilisable (la clé n'est pas consommée).
  if (accessKey.passkey === false) {
    await startSession(accessKey.user_id);
    return { ok: true };
  }

  if (accessKey.used_at) {
    return { error: 'This key has already been used. Sign in with your passkey.' };
  }

  const { data: existing } = await admin
    .from('passkeys')
    .select('id, transports')
    .eq('user_id', accessKey.user_id);
  if ((existing?.length ?? 0) >= MAX_PASSKEYS) {
    return { error: 'This account already has the maximum number of devices.' };
  }

  const { rpID } = await getRelyingParty();
  const options = await generateRegistrationOptions({
    rpName: RP_NAME,
    rpID,
    userName: accessKey.profiles.username,
    userID: new TextEncoder().encode(accessKey.user_id),
    attestationType: 'none',
    excludeCredentials: (existing ?? []).map((p) => ({ id: p.id, transports: p.transports })),
    authenticatorSelection: { residentKey: 'required', userVerification: 'required' },
  });

  await saveChallenge({
    kind: 'register',
    challenge: options.challenge,
    userId: accessKey.user_id,
    accessKeyId: accessKey.id,
  });
  return { options };
}

export async function finishActivation(response) {
  const pending = await takeChallenge('register');
  if (!pending) return { error: EXPIRED };

  const { origin, rpID } = await getRelyingParty();
  let verification;
  try {
    verification = await verifyRegistrationResponse({
      response,
      expectedChallenge: pending.challenge,
      expectedOrigin: origin,
      expectedRPID: rpID,
      requireUserVerification: true,
    });
  } catch {
    return { error: 'Passkey could not be verified. Try again.' };
  }
  if (!verification.verified) return { error: 'Passkey could not be verified. Try again.' };

  const admin = createAdminClient();
  const { count } = await admin
    .from('passkeys')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', pending.user_id);
  if ((count ?? 0) >= MAX_PASSKEYS) {
    return { error: 'This account already has the maximum number of devices.' };
  }

  // Réservation atomique : deux activations simultanées ne peuvent pas consommer la même clé.
  const { data: claimed } = await admin
    .from('access_keys')
    .update({ used_at: new Date().toISOString() })
    .eq('id', pending.access_key_id)
    .is('used_at', null)
    .eq('revoked', false)
    .or(`expires_at.is.null,expires_at.gt."${new Date().toISOString()}"`)
    .select('id')
    .maybeSingle();
  if (!claimed) return { error: INVALID_KEY };

  const { credential, credentialDeviceType, credentialBackedUp } = verification.registrationInfo;
  const { error } = await admin.from('passkeys').insert({
    id: credential.id,
    user_id: pending.user_id,
    public_key: toBase64Url(credential.publicKey),
    counter: credential.counter,
    transports: credential.transports ?? [],
    device_type: credentialDeviceType,
    backed_up: credentialBackedUp,
  });
  if (error) {
    await admin.from('access_keys').update({ used_at: null }).eq('id', claimed.id);
    return { error: 'Passkey could not be saved. Try again.' };
  }

  await startSession(pending.user_id);
  return { ok: true };
}

export async function beginPasskeyLogin() {
  if (await isRateLimited()) return { error: RATE_LIMITED };

  const { rpID } = await getRelyingParty();
  const options = await generateAuthenticationOptions({ rpID, userVerification: 'required' });
  await saveChallenge({ kind: 'login', challenge: options.challenge });
  return { options };
}

export async function finishPasskeyLogin(response) {
  const pending = await takeChallenge('login');
  if (!pending) return { error: EXPIRED };

  const admin = createAdminClient();
  const { data: passkey } = await admin
    .from('passkeys')
    .select('id, user_id, public_key, counter, transports')
    .eq('id', response?.id ?? '')
    .maybeSingle();
  if (!passkey) return { error: 'This passkey is not registered on Argos.' };

  const { origin, rpID } = await getRelyingParty();
  let verification;
  try {
    verification = await verifyAuthenticationResponse({
      response,
      expectedChallenge: pending.challenge,
      expectedOrigin: origin,
      expectedRPID: rpID,
      requireUserVerification: true,
      credential: {
        id: passkey.id,
        publicKey: fromBase64Url(passkey.public_key),
        counter: Number(passkey.counter),
        transports: passkey.transports,
      },
    });
  } catch {
    return { error: 'Passkey could not be verified. Try again.' };
  }
  if (!verification.verified) return { error: 'Passkey could not be verified. Try again.' };

  await admin
    .from('passkeys')
    .update({
      counter: verification.authenticationInfo.newCounter,
      last_used_at: new Date().toISOString(),
    })
    .eq('id', passkey.id);

  await startSession(passkey.user_id);
  return { ok: true };
}

export async function signOut() {
  await endSession();
  redirect('/login');
}
